import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import {
  CalculationStatus,
  INIT_MODES,
  IReportTabState,
  InitMode,
  StateField,
} from '@navstation-portal/reporting/data-model/vessel-report';
import {
  CargoPumpMeterFormState,
  CargoPumpMeterState,
  EngineMode,
  GeneratorEngineFormState,
  GeneratorEngineState,
  MachineryRhFormState,
  MachineryRhState,
  MainEngineState,
  PowerState,
  ShaftGeneratorFormState,
  ShaftGeneratorState,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { DropboxItem } from '@navstation-portal/shared/data-model/common';
import { VesselSpecStore } from '@navstation-portal/vessels/data-access/store';
import { VesselSpec } from '@navstation-portal/vessels/data-model/vessel';
import { signalStore, withComputed, withFeature, withState } from '@ngrx/signals';
import { initStateField } from '../../utils/store.utility';
import { OperationalStore } from '../operational/operational.store';
import { EdnFileType } from '../report-dependencies';
import { withCalculatedValues } from '../with-calculated-values';
import { withCalculationReducer } from '../with-calculation-reducer';
import { withFormStoreReducer } from '../with-form-store-reducer';
import { collectPowerCalculatedValues } from './power-calculated-values';
import { powerCalculationEvents, powerEvents } from './power-events';
import { buildPowerVm } from './power-vm-builder';
import { withPowerEffects } from './with-power-effects';
import { withPowerReducer, withShorePowerDocumentReducer, withTcRpmPowerReducer } from './with-power-reducer';

const initPowerStateReducer = (
  initialData: IReportTabState,
  initMode: InitMode,
  vesselSpec: VesselSpec | undefined
) => {
  const data = initialData as PowerState;
  switch (initMode) {
    case INIT_MODES.CARRY_OVER:
      return initWithCarryOver(data, vesselSpec);
    case INIT_MODES.IMPORT:
      return initWithExistingReport(data, vesselSpec);
    case INIT_MODES.BLANK:
      return initBlank(vesselSpec);
    default:
      return initBlank(vesselSpec);
  }
};

// The Calculated Values of the Main Engine section. A read-only control
// calculates each one and writes it here, and the save sends it. An imported
// report starts with its saved values, so a save before the user opens the
// Power tab sends them back unchanged.
const initMainEngineCalculatedValues = (data?: MainEngineState) => ({
  mainEngineLoad: initStateField<number>(data?.mainEngineLoad),
  mainEngineRevCounterDiff: initStateField<number>(data?.mainEngineRevCounterDiff),
  mainEngineCalculatedRpm: initStateField<number>(data?.mainEngineCalculatedRpm),
  mainEngines1And2Power: initStateField<number>(data?.mainEngines1And2Power),
  mainEngines1And2Load: initStateField<number>(data?.mainEngines1And2Load),
  mainEngines1And2RunningHours: initStateField<number>(data?.mainEngines1And2RunningHours),
  shaftPowerMeterCounterDifference: initStateField<number>(data?.shaftPowerMeterCounterDifference),
  shaftPowerMeterCounterCalculated: initStateField<number>(data?.shaftPowerMeterCounterCalculated),
});

const generateMachineryRhObject = (data: MachineryRhState, isCarryOver = true) => {
  const initialPrevious = isCarryOver ? data.current : data.previous;
  const previousWasEmpty = initialPrevious == null;
  const machineryRh: MachineryRhFormState = {
    key: initStateField<string>(data.key || ''),
    label: initStateField<string>(data?.label),
    current: initStateField<number>(isCarryOver ? undefined : data.current),
    previous: initStateField<number>(isCarryOver ? (initialPrevious ?? 0) : initialPrevious),
    difference: initStateField<number>(),
    previousWasEmpty: initStateField(previousWasEmpty),
  };
  return machineryRh;
};

const generateMachineryRhArray = (key: string, data: MachineryRhState[], isCarryOver: boolean) => {
  const result: MachineryRhFormState[] = data
    .filter((item) => item.key.startsWith(key))
    .map((item) => {
      return generateMachineryRhObject(item, isCarryOver);
    });

  return result;
};

// Rows that always exist, so a vessel with no saved value can type a first
// reading. The form rule template decides whether each one shows.
// keepPrevious carries the prior previous reading forward while the prior
// report holds no current reading, for a row that hides on some report types.
const generateFixedMachineryRhRows = (
  rows: { key: string; label: string }[],
  data: MachineryRhState[] | undefined,
  isCarryOver: boolean,
  keepPrevious = false
): MachineryRhFormState[] =>
  rows.map(({ key, label }) => {
    const item = data?.find((entry) => entry.key === key) ?? { key, label };
    const carried = isCarryOver && keepPrevious ? { ...item, current: item.current ?? item.previous } : item;
    return generateMachineryRhObject(carried, isCarryOver);
  });

const COMPOSITE_BOILER_ROWS = [{ key: 'compositeBoiler', label: 'Composite Boiler' }];

// V1 holds 4 main air compressors (power.html:1832-1927 in ../TVA-Reporting).
const MAIN_AIR_COMPRESSOR_ROWS = Array.from({ length: 4 }, (_, i) => ({
  key: `mainAirComp${i + 1}`,
  label: `Main Air Comp. ${i + 1}`,
}));

// V1 holds up to 10 cargo compressors (reports.service.ts:808-817 in
// ../TVA-Reporting).
const CARGO_COMPRESSOR_ROWS = Array.from({ length: 10 }, (_, i) => ({
  key: `cargoCompressor${i + 1}`,
  label: `Cargo Compressor ${i + 1}`,
}));

// V1 holds up to 10 BOG compressors (reports.service.ts:818-827 in
// ../TVA-Reporting).
const BOG_COMPRESSOR_ROWS = Array.from({ length: 10 }, (_, i) => ({
  key: `bogCompressor${i + 1}`,
  label: `BOG Compressor ${i + 1}`,
}));

// V1 holds 8 cargo pumps, and shows them on a Port report only. On another
// report it copies the reading on (reports.service.ts:828-849 in
// ../TVA-Reporting), so the rows keep the prior previous reading.
const CARGO_PUMP_ROWS = Array.from({ length: 8 }, (_, i) => ({
  key: `cargoPump${i + 1}`,
  label: `Cargo Pump ${i + 1}`,
}));

// V1 holds 2 ballast pumps, and copies the reading on as it does for the
// cargo pumps (reports.service.ts:836-849 in ../TVA-Reporting).
const BALLAST_PUMP_ROWS = Array.from({ length: 2 }, (_, i) => ({
  key: `ballastPump${i + 1}`,
  label: `Ballast Pump ${i + 1}`,
}));

// V1 shows the thrusters on a Maneuvering report only, so the Sea reports
// between two Maneuvering reports save no current reading.
const THRUSTER_ROWS = [
  { key: 'bowThruster', label: 'Bow Thruster' },
  { key: 'sternThruster', label: 'Stern Thruster' },
];

// V1 copies the current reading to previous, or the previous reading
// (reports.service.ts:705-706 in ../TVA-Reporting).
const SCR_ROWS = [{ key: 'scr', label: 'Selective Catalytic Reduction' }];

const generateMachineryRhArrays = (
  data: MachineryRhState[] | undefined,
  isCarryOver: boolean,
  vesselSpec: VesselSpec | undefined
) => {
  if (!data)
    return {
      mainEngines: [] as MachineryRhFormState[],
      auxEngines: [] as MachineryRhFormState[],
      mainAirCompressors: generateFixedMachineryRhRows(MAIN_AIR_COMPRESSOR_ROWS, undefined, isCarryOver),
      auxBoilers: [] as MachineryRhFormState[],
      compositeBoilers: generateFixedMachineryRhRows(COMPOSITE_BOILER_ROWS, undefined, isCarryOver),
      cargoCompressors: generateFixedMachineryRhRows(CARGO_COMPRESSOR_ROWS, undefined, isCarryOver),
      bogCompressors: generateFixedMachineryRhRows(BOG_COMPRESSOR_ROWS, undefined, isCarryOver),
      cargoPumps: generateFixedMachineryRhRows(CARGO_PUMP_ROWS, undefined, isCarryOver, true),
      ballastPumps: generateFixedMachineryRhRows(BALLAST_PUMP_ROWS, undefined, isCarryOver, true),
      thrusters: generateFixedMachineryRhRows(THRUSTER_ROWS, undefined, isCarryOver, true),
      scr: generateFixedMachineryRhRows(SCR_ROWS, undefined, isCarryOver, true),
      shaftGeneratorPTO: [] as MachineryRhFormState[],
    };

  const numberOfAuxBoilers = vesselSpec?.engine?.number_of_aux_boilers;
  const auxBoilers = generateMachineryRhArray('auxBoiler', data, isCarryOver);

  return {
    mainEngines: generateMachineryRhArray('mainEngine', data, isCarryOver),
    auxEngines: generateMachineryRhArray('auxEngine', data, isCarryOver),
    mainAirCompressors: generateFixedMachineryRhRows(MAIN_AIR_COMPRESSOR_ROWS, data, isCarryOver),
    auxBoilers: numberOfAuxBoilers == null ? auxBoilers : auxBoilers.slice(0, numberOfAuxBoilers),
    compositeBoilers: generateFixedMachineryRhRows(COMPOSITE_BOILER_ROWS, data, isCarryOver),
    cargoCompressors: generateFixedMachineryRhRows(CARGO_COMPRESSOR_ROWS, data, isCarryOver),
    bogCompressors: generateFixedMachineryRhRows(BOG_COMPRESSOR_ROWS, data, isCarryOver),
    cargoPumps: generateFixedMachineryRhRows(CARGO_PUMP_ROWS, data, isCarryOver, true),
    ballastPumps: generateFixedMachineryRhRows(BALLAST_PUMP_ROWS, data, isCarryOver, true),
    thrusters: generateFixedMachineryRhRows(THRUSTER_ROWS, data, isCarryOver, true),
    scr: generateFixedMachineryRhRows(SCR_ROWS, data, isCarryOver, true),
    shaftGeneratorPTO: generateMachineryRhArray('shaftGeneratorPTO', data, isCarryOver),
  };
};

const generateCargoPumpMeterEntry = (data: CargoPumpMeterState, isCarryOver: boolean): CargoPumpMeterFormState => {
  const initialPrevious = isCarryOver ? data.cur : data.prev;
  const previousWasEmpty = initialPrevious == null;
  return {
    cur: initStateField<number>(isCarryOver ? undefined : data.cur),
    prev: initStateField<number>(isCarryOver ? (initialPrevious ?? 0) : initialPrevious),
    previousWasEmpty: initStateField(previousWasEmpty),
    // Mirrors getCargoPumpMeterDiff's live cur/prev computation — see the
    // comment on CargoPumpMeterFormState.diff for why this must exist.
    diff: initStateField<number>(),
  };
};

const getCargoPumpCount = (vesselSpec: VesselSpec | undefined) =>
  vesselSpec?.information?.number_of_marine_cargo_pumps ?? 0;

// Submitted report: the pump count it was saved with is fixed, regardless of
// what the vessel spec says now — so a later change to the vessel's pump
// count doesn't retroactively alter a legacy report.
const generateCargoPumpsKwhArray = (data: CargoPumpMeterState[] | undefined): CargoPumpMeterFormState[] =>
  (data ?? []).map((entry) => generateCargoPumpMeterEntry(entry, false));

// New, unsubmitted draft seeded from a prior report: row count follows the
// current vessel spec, pre-filled by index from the prior report's readings
// where available.
const generateCargoPumpsForCarryOver = (
  data: CargoPumpMeterState[] | undefined,
  vesselSpec: VesselSpec | undefined
): CargoPumpMeterFormState[] =>
  Array.from({ length: getCargoPumpCount(vesselSpec) }, (_, i) => generateCargoPumpMeterEntry(data?.[i] ?? {}, true));

const generateGeneratorEngineObject = (data: GeneratorEngineState) => {
  const generatorEngine: GeneratorEngineFormState = {
    key: initStateField<string>(data.key || ''),
    label: initStateField<string>(data?.label),
    power: initStateField<number>(data?.power),
    maxRating: initStateField<number>(data?.maxRating),
    runningHours: initStateField<number>(data?.runningHours),
    chargeAirPressure: initStateField<number>(data?.chargeAirPressure),
    loAdded: initStateField<number>(data?.loAdded),
  };
  return generatorEngine;
};

const generateGeneratorEnginesArray = (key: string, data: GeneratorEngineState[]) => {
  const result: GeneratorEngineFormState[] = data
    .filter((item) => item.key.startsWith(key))
    .map((item) => {
      return generateGeneratorEngineObject(item);
    });

  return result;
};

const generateGeneratorEnginesArrays = (data?: GeneratorEngineState[]) => {
  if (!data)
    return {
      auxEngines: [] as GeneratorEngineFormState[],
    };

  return {
    auxEngines: generateGeneratorEnginesArray('auxEngine', data),
  };
};

const generateShaftGeneratorPTO = (data?: ShaftGeneratorState): ShaftGeneratorFormState => {
  return {
    key: initStateField<string>(data?.key || 'shaftGeneratorPTO'),
    power: initStateField<number>(data?.power),
    maxRating: initStateField<number>(data?.maxRating),
    runningHours: initStateField<number>(data?.runningHours),
  };
};

const buildFlowMeterInstallationSection = (readings?: Record<string, number>) => {
  const section: Record<string, StateField<number | undefined>> = {};
  if (!readings) return section;

  for (const [flowPath, value] of Object.entries(readings)) {
    section[flowPath] = initStateField<number>(value);
  }
  return section;
};

export const isShorePowerCarryOver = (carryOverShorePower: PowerState['shorePower'] | undefined): boolean =>
  !!carryOverShorePower?.isUsed && !carryOverShorePower?.endTime;

// Reception port and the "default to used" behaviour for new port/anchor reports are
// applied afterwards in withReportEventHandlers, once the operational port and report type
// are known — only the fields tied to an ongoing session carry forward here.
const generateCarryOverShorePower = (priorShorePower: PowerState['shorePower'] | undefined) => {
  const wasOngoing = isShorePowerCarryOver(priorShorePower);
  return {
    ...generateShorePowerBlank(),
    isUsed: initStateField<boolean>(wasOngoing ? true : undefined),
    receptionPort: initStateField<string>(priorShorePower?.receptionPort),
    startTime: initStateField<Date>(wasOngoing ? priorShorePower?.startTime : undefined),
    runningTotalKwh: initStateField<number>(wasOngoing ? (priorShorePower?.runningTotalKwh ?? 0) : undefined),
    // Must carry forward with the session or the backend's running_total_shore_power_kwh
    // aggregation (keyed on edn_id) can't find this session's earlier completed reports.
    ednId: initStateField<string>(wasOngoing ? priorShorePower?.ednId : undefined),
  };
};

export type ShorePowerFormFieldId = keyof ReturnType<typeof generateShorePowerBlank>;

const generateShorePowerBlank = () => ({
  isUsed: initStateField<boolean>(),
  receptionPort: initStateField<string>(),
  kwh: initStateField<number>(),
  hours: initStateField<number>(),
  startTime: initStateField<Date>(),
  endTime: initStateField<Date>(),
  totalKwh: initStateField<number>(),
  wtt: initStateField<number>(),
  ednNumber: initStateField<string>(),
  ednFiles: initStateField<DropboxItem[]>([]),
  sustainableIsUsed: initStateField<boolean>(),
  proofOfSustainabilityEdnFiles: initStateField<DropboxItem[]>([]),
  runningTotalKwh: initStateField<number>(),
  ednId: initStateField<string>(),
});

const generateTurboGenerator = (data?: GeneratorEngineState[]) => {
  const turboGenerator = data?.find((item) => item.key?.startsWith('turboGenerator'));

  return {
    turboPower: initStateField<number>(turboGenerator?.power),
    turboMaxRating: initStateField<number>(turboGenerator?.maxRating),
    turboRunningHours: initStateField<number>(turboGenerator?.runningHours),
    turboChargeAirPressure: initStateField<number>(turboGenerator?.chargeAirPressure),
  };
};

const initBlank = (vesselSpec?: VesselSpec) => {
  const cargoPumpCount = getCargoPumpCount(vesselSpec);
  return {
    formState: {
      mainEngine: {
        power: initStateField<number>(),
        rpm: initStateField<number>(),
        revCounterCurrent: initStateField<number>(),
        revCounterPrevious: initStateField<number>(),
        shaftPowerMeterCounterCurrent: initStateField<number>(),
        shaftPowerMeterCounterPrevious: initStateField<number>(),
        shaftPowerMeterCounterCalculatedManual: initStateField<number>(),
        tcRpm1: initStateField<number>(),
        tcRpm2: initStateField<number>(),
        tcRpmPower: initStateField<number>(),
        engineModes: initStateField<EngineMode[]>(),
        ...initMainEngineCalculatedValues(),
      },
      machineryRunningHours: {
        ...generateMachineryRhArrays(undefined, false, undefined),
      },
      flowMeterInstallationOld: {
        foBoosterCurr: initStateField<number>(),
        foBoosterPrev: initStateField<number>(),
        foBoosterDiff: initStateField<number>(),
        gasOilBoosterCurr: initStateField<number>(),
        gasOilBoosterPrev: initStateField<number>(),
        gasOilBoosterDiff: initStateField<number>(),
        meAeInletCurr: initStateField<number>(),
        meAeInletPrev: initStateField<number>(),
        meAeInletDiff: initStateField<number>(),
        auxEngineInletCurr: initStateField<number>(),
        auxEngineInletPrev: initStateField<number>(),
        auxEngineInletDiff: initStateField<number>(),
        auxEngineOutletCurr: initStateField<number>(),
        auxEngineOutletPrev: initStateField<number>(),
        auxEngineOutletDiff: initStateField<number>(),
        auxBoiler1Curr: initStateField<number>(),
        auxBoiler1Prev: initStateField<number>(),
        auxBoiler1Diff: initStateField<number>(),
        cylinderLubeOilCurr: initStateField<number>(),
        cylinderLubeOilPrev: initStateField<number>(),
        cylinderLubeOilDiff: initStateField<number>(),
      },
      flowMeterInstallation: buildFlowMeterInstallationSection(),
      pumps: {
        reportPumpSection: initStateField<boolean>(),
        meLoPump1Curr: initStateField<number>(),
        meLoPump1Prev: initStateField<number>(),
        meLoPump1Diff: initStateField<number>(),
        meLoPump2Curr: initStateField<number>(),
        meLoPump2Prev: initStateField<number>(),
        meLoPump2Diff: initStateField<number>(),
        seaWaterPump1Curr: initStateField<number>(),
        seaWaterPump1Prev: initStateField<number>(),
        seaWaterPump1Diff: initStateField<number>(),
        seaWaterPump2Curr: initStateField<number>(),
        seaWaterPump2Prev: initStateField<number>(),
        seaWaterPump2Diff: initStateField<number>(),
        sgPump1Curr: initStateField<number>(),
        sgPump1Prev: initStateField<number>(),
        sgPump1Diff: initStateField<number>(),
        sgPump2Curr: initStateField<number>(),
        sgPump2Prev: initStateField<number>(),
        sgPump2Diff: initStateField<number>(),
        foPurifier1Curr: initStateField<number>(),
        foPurifier1Prev: initStateField<number>(),
        foPurifier1Diff: initStateField<number>(),
        foPurifier2Curr: initStateField<number>(),
        foPurifier2Prev: initStateField<number>(),
        foPurifier2Diff: initStateField<number>(),
        erFan1Curr: initStateField<number>(),
        erFan1Prev: initStateField<number>(),
        erFan1Diff: initStateField<number>(),
        erFan2Curr: initStateField<number>(),
        erFan2Prev: initStateField<number>(),
        erFan2Diff: initStateField<number>(),
        erFan3Curr: initStateField<number>(),
        erFan3Prev: initStateField<number>(),
        erFan3Diff: initStateField<number>(),
        erFan4Curr: initStateField<number>(),
        erFan4Prev: initStateField<number>(),
        erFan4Diff: initStateField<number>(),
      },
      generatorEngines: {
        ...generateGeneratorEnginesArrays(undefined),
        ...generateTurboGenerator(undefined),
      },
      shaftGeneratorPTO: generateShaftGeneratorPTO(undefined),
      scrubber: {
        inUse: initStateField<boolean>(),
        mode: initStateField<string>(),
        runningHours: initStateField<number>(),
      },
      environmental: {
        incineratorMalfunctional: initStateField<boolean>(),
        odmeMalfunctional: initStateField<boolean>(),
        bwtsMalfunctional: initStateField<boolean>(),
        scrubberMalfunctional: initStateField<boolean>(),
        owsMalfunctional: initStateField<boolean>(),
      },
      shorePower: generateShorePowerBlank(),
      cargoPumpMeters: {
        pumps: Array.from({ length: cargoPumpCount }, () => generateCargoPumpMeterEntry({}, false)),
      },
    },
    calculations: {} as Record<string, CalculationStatus>,
  };
};

const initWithExistingReport = (data: PowerState, vesselSpec: VesselSpec | undefined) => {
  return {
    formState: {
      mainEngine: {
        power: initStateField<number>(data.mainEngine.power),
        rpm: initStateField<number>(data.mainEngine.rpm),
        revCounterCurrent: initStateField<number>(data.mainEngine.revCounterCurrent),
        revCounterPrevious: initStateField<number>(data.mainEngine.revCounterPrevious),
        shaftPowerMeterCounterCurrent: initStateField<number>(data.mainEngine.shaftPowerMeterCounterCurrent),
        shaftPowerMeterCounterPrevious: initStateField<number>(data.mainEngine.shaftPowerMeterCounterPrevious),
        shaftPowerMeterCounterCalculatedManual: initStateField<number>(
          data.mainEngine.shaftPowerMeterCounterCalculatedManual
        ),
        tcRpm1: initStateField<number>(data.mainEngine.tcRpm1),
        tcRpm2: initStateField<number>(data.mainEngine.tcRpm2),
        tcRpmPower: initStateField<number>(data.mainEngine.tcRpmPower),
        engineModes: initStateField<EngineMode[]>(data.mainEngine.engineModes),
        ...initMainEngineCalculatedValues(data.mainEngine),
      },
      machineryRunningHours: {
        ...generateMachineryRhArrays(data.machineryRh, false, vesselSpec),
      },
      flowMeterInstallationOld: {
        foBoosterCurr: initStateField<number>(data.flowMeterInstallationOld.foBoosterCurr),
        foBoosterPrev: initStateField<number>(data.flowMeterInstallationOld.foBoosterPrev),
        foBoosterDiff: initStateField<number>(data.flowMeterInstallationOld.foBoosterDiff),
        gasOilBoosterCurr: initStateField<number>(data.flowMeterInstallationOld.gasOilBoosterCurr),
        gasOilBoosterPrev: initStateField<number>(data.flowMeterInstallationOld.gasOilBoosterPrev),
        gasOilBoosterDiff: initStateField<number>(data.flowMeterInstallationOld.gasOilBoosterDiff),
        meAeInletCurr: initStateField<number>(data.flowMeterInstallationOld.meAeInletCurr),
        meAeInletPrev: initStateField<number>(data.flowMeterInstallationOld.meAeInletPrev),
        meAeInletDiff: initStateField<number>(data.flowMeterInstallationOld.meAeInletDiff),
        auxEngineInletCurr: initStateField<number>(data.flowMeterInstallationOld.auxEngineInletCurr),
        auxEngineInletPrev: initStateField<number>(data.flowMeterInstallationOld.auxEngineInletPrev),
        auxEngineInletDiff: initStateField<number>(data.flowMeterInstallationOld.auxEngineInletDiff),
        auxEngineOutletCurr: initStateField<number>(data.flowMeterInstallationOld.auxEngineOutletCurr),
        auxEngineOutletPrev: initStateField<number>(data.flowMeterInstallationOld.auxEngineOutletPrev),
        auxEngineOutletDiff: initStateField<number>(data.flowMeterInstallationOld.auxEngineOutletDiff),
        auxBoiler1Curr: initStateField<number>(data.flowMeterInstallationOld.auxBoiler1Curr),
        auxBoiler1Prev: initStateField<number>(data.flowMeterInstallationOld.auxBoiler1Prev),
        auxBoiler1Diff: initStateField<number>(data.flowMeterInstallationOld.auxBoiler1Diff),
        cylinderLubeOilCurr: initStateField<number>(data.flowMeterInstallationOld.cylinderLubeOilCurr),
        cylinderLubeOilPrev: initStateField<number>(data.flowMeterInstallationOld.cylinderLubeOilPrev),
        cylinderLubeOilDiff: initStateField<number>(data.flowMeterInstallationOld.cylinderLubeOilDiff),
      },
      flowMeterInstallation: buildFlowMeterInstallationSection(data.flowMeterInstallation),
      pumps: {
        reportPumpSection: initStateField<boolean>(data.pumps.reportPumpSection),
        meLoPump1Curr: initStateField<number>(data.pumps.meLoPump1Curr),
        meLoPump1Prev: initStateField<number>(data.pumps.meLoPump1Prev),
        meLoPump1Diff: initStateField<number>(data.pumps.meLoPump1Diff),
        meLoPump2Curr: initStateField<number>(data.pumps.meLoPump2Curr),
        meLoPump2Prev: initStateField<number>(data.pumps.meLoPump2Prev),
        meLoPump2Diff: initStateField<number>(data.pumps.meLoPump2Diff),
        seaWaterPump1Curr: initStateField<number>(data.pumps.seaWaterPump1Curr),
        seaWaterPump1Prev: initStateField<number>(data.pumps.seaWaterPump1Prev),
        seaWaterPump1Diff: initStateField<number>(data.pumps.seaWaterPump1Diff),
        seaWaterPump2Curr: initStateField<number>(data.pumps.seaWaterPump2Curr),
        seaWaterPump2Prev: initStateField<number>(data.pumps.seaWaterPump2Prev),
        seaWaterPump2Diff: initStateField<number>(data.pumps.seaWaterPump2Diff),
        sgPump1Curr: initStateField<number>(data.pumps.sgPump1Curr),
        sgPump1Prev: initStateField<number>(data.pumps.sgPump1Prev),
        sgPump1Diff: initStateField<number>(data.pumps.sgPump1Diff),
        sgPump2Curr: initStateField<number>(data.pumps.sgPump2Curr),
        sgPump2Prev: initStateField<number>(data.pumps.sgPump2Prev),
        sgPump2Diff: initStateField<number>(data.pumps.sgPump2Diff),
        foPurifier1Curr: initStateField<number>(data.pumps.foPurifier1Curr),
        foPurifier1Prev: initStateField<number>(data.pumps.foPurifier1Prev),
        foPurifier1Diff: initStateField<number>(data.pumps.foPurifier1Diff),
        foPurifier2Curr: initStateField<number>(data.pumps.foPurifier2Curr),
        foPurifier2Prev: initStateField<number>(data.pumps.foPurifier2Prev),
        foPurifier2Diff: initStateField<number>(data.pumps.foPurifier2Diff),
        erFan1Curr: initStateField<number>(data.pumps.erFan1Curr),
        erFan1Prev: initStateField<number>(data.pumps.erFan1Prev),
        erFan1Diff: initStateField<number>(data.pumps.erFan1Diff),
        erFan2Curr: initStateField<number>(data.pumps.erFan2Curr),
        erFan2Prev: initStateField<number>(data.pumps.erFan2Prev),
        erFan2Diff: initStateField<number>(data.pumps.erFan2Diff),
        erFan3Curr: initStateField<number>(data.pumps.erFan3Curr),
        erFan3Prev: initStateField<number>(data.pumps.erFan3Prev),
        erFan3Diff: initStateField<number>(data.pumps.erFan3Diff),
        erFan4Curr: initStateField<number>(data.pumps.erFan4Curr),
        erFan4Prev: initStateField<number>(data.pumps.erFan4Prev),
        erFan4Diff: initStateField<number>(data.pumps.erFan4Diff),
      },
      generatorEngines: {
        ...generateGeneratorEnginesArrays(data.generatorEngines),
        ...generateTurboGenerator(data.generatorEngines),
      },
      shaftGeneratorPTO: generateShaftGeneratorPTO(data.shaftGeneratorPTO),
      scrubber: {
        inUse: initStateField<boolean>(data.scrubber.inUse ?? false),
        mode: initStateField<string>(data.scrubber.mode),
        runningHours: initStateField<number>(data.scrubber.runningHours),
      },
      environmental: {
        incineratorMalfunctional: initStateField<boolean>(data.environmental.incineratorMalfunctional ?? false),
        odmeMalfunctional: initStateField<boolean>(data.environmental.odmeMalfunctional ?? false),
        bwtsMalfunctional: initStateField<boolean>(data.environmental.bwtsMalfunctional ?? false),
        scrubberMalfunctional: initStateField<boolean>(data.environmental.scrubberMalfunctional ?? false),
        owsMalfunctional: initStateField<boolean>(data.environmental.owsMalfunctional ?? false),
      },
      shorePower: {
        isUsed: initStateField<boolean>(data.shorePower?.isUsed),
        receptionPort: initStateField<string>(data.shorePower?.receptionPort),
        kwh: initStateField<number>(data.shorePower?.kwh),
        hours: initStateField<number>(data.shorePower?.hours),
        startTime: initStateField<Date>(data.shorePower?.startTime),
        endTime: initStateField<Date>(data.shorePower?.endTime),
        totalKwh: initStateField<number>(data.shorePower?.totalKwh),
        wtt: initStateField<number>(data.shorePower?.wtt),
        ednNumber: initStateField<string>(data.shorePower?.ednNumber),
        ednFiles: initStateField<DropboxItem[]>(data.shorePower?.ednFiles ?? []),
        sustainableIsUsed: initStateField<boolean>(data.shorePower?.sustainableIsUsed ?? false),
        proofOfSustainabilityEdnFiles: initStateField<DropboxItem[]>(
          data.shorePower?.proofOfSustainabilityEdnFiles ?? []
        ),
        runningTotalKwh: initStateField<number>(data.shorePower?.runningTotalKwh),
        ednId: initStateField<string>(data.shorePower?.ednId),
      },
      cargoPumpMeters: {
        pumps: generateCargoPumpsKwhArray(data.cargoPumpsKwh),
      },
    },
    calculations: {} as Record<string, CalculationStatus>,
  };
};

const initWithCarryOver = (data: PowerState, vesselSpec: VesselSpec | undefined) => {
  return {
    formState: {
      mainEngine: {
        power: initStateField<number>(),
        rpm: initStateField<number>(),
        revCounterCurrent: initStateField<number>(),
        revCounterPrevious: initStateField<number>(data.mainEngine.revCounterCurrent),
        shaftPowerMeterCounterCurrent: initStateField<number>(),
        shaftPowerMeterCounterPrevious: initStateField<number>(data.mainEngine.shaftPowerMeterCounterCurrent),
        shaftPowerMeterCounterCalculatedManual: initStateField<number>(),
        tcRpm1: initStateField<number>(),
        tcRpm2: initStateField<number>(),
        tcRpmPower: initStateField<number>(),
        engineModes: initStateField<EngineMode[]>(),
        ...initMainEngineCalculatedValues(),
      },
      machineryRunningHours: {
        ...generateMachineryRhArrays(data.machineryRh, true, vesselSpec),
      },
      flowMeterInstallationOld: {
        foBoosterCurr: initStateField<number>(),
        foBoosterPrev: initStateField<number>(data.flowMeterInstallationOld.foBoosterCurr),
        foBoosterDiff: initStateField<number>(),
        gasOilBoosterCurr: initStateField<number>(),
        gasOilBoosterPrev: initStateField<number>(data.flowMeterInstallationOld.gasOilBoosterCurr),
        gasOilBoosterDiff: initStateField<number>(),
        meAeInletCurr: initStateField<number>(),
        meAeInletPrev: initStateField<number>(data.flowMeterInstallationOld.meAeInletCurr),
        meAeInletDiff: initStateField<number>(),
        auxEngineInletCurr: initStateField<number>(),
        auxEngineInletPrev: initStateField<number>(data.flowMeterInstallationOld.auxEngineInletCurr),
        auxEngineInletDiff: initStateField<number>(),
        auxEngineOutletCurr: initStateField<number>(),
        auxEngineOutletPrev: initStateField<number>(data.flowMeterInstallationOld.auxEngineOutletCurr),
        auxEngineOutletDiff: initStateField<number>(),
        auxBoiler1Curr: initStateField<number>(),
        auxBoiler1Prev: initStateField<number>(data.flowMeterInstallationOld.auxBoiler1Curr),
        auxBoiler1Diff: initStateField<number>(),
        cylinderLubeOilCurr: initStateField<number>(),
        cylinderLubeOilPrev: initStateField<number>(data.flowMeterInstallationOld.cylinderLubeOilCurr),
        cylinderLubeOilDiff: initStateField<number>(),
      },
      flowMeterInstallation: buildFlowMeterInstallationSection(data.flowMeterInstallation),
      pumps: {
        reportPumpSection: initStateField<boolean>(data.pumps.reportPumpSection),
        meLoPump1Curr: initStateField<number>(),
        meLoPump1Prev: initStateField<number>(data.pumps.meLoPump1Curr),
        meLoPump1Diff: initStateField<number>(),
        meLoPump2Curr: initStateField<number>(),
        meLoPump2Prev: initStateField<number>(data.pumps.meLoPump2Curr),
        meLoPump2Diff: initStateField<number>(),
        seaWaterPump1Curr: initStateField<number>(),
        seaWaterPump1Prev: initStateField<number>(data.pumps.seaWaterPump1Curr),
        seaWaterPump1Diff: initStateField<number>(),
        seaWaterPump2Curr: initStateField<number>(),
        seaWaterPump2Prev: initStateField<number>(data.pumps.seaWaterPump2Curr),
        seaWaterPump2Diff: initStateField<number>(),
        sgPump1Curr: initStateField<number>(),
        sgPump1Prev: initStateField<number>(data.pumps.sgPump1Curr),
        sgPump1Diff: initStateField<number>(),
        sgPump2Curr: initStateField<number>(),
        sgPump2Prev: initStateField<number>(data.pumps.sgPump2Curr),
        sgPump2Diff: initStateField<number>(),
        foPurifier1Curr: initStateField<number>(),
        foPurifier1Prev: initStateField<number>(data.pumps.foPurifier1Curr),
        foPurifier1Diff: initStateField<number>(),
        foPurifier2Curr: initStateField<number>(),
        foPurifier2Prev: initStateField<number>(data.pumps.foPurifier2Curr),
        foPurifier2Diff: initStateField<number>(),
        erFan1Curr: initStateField<number>(),
        erFan1Prev: initStateField<number>(data.pumps.erFan1Curr),
        erFan1Diff: initStateField<number>(),
        erFan2Curr: initStateField<number>(),
        erFan2Prev: initStateField<number>(data.pumps.erFan2Curr),
        erFan2Diff: initStateField<number>(),
        erFan3Curr: initStateField<number>(),
        erFan3Prev: initStateField<number>(data.pumps.erFan3Curr),
        erFan3Diff: initStateField<number>(),
        erFan4Curr: initStateField<number>(),
        erFan4Prev: initStateField<number>(data.pumps.erFan4Curr),
        erFan4Diff: initStateField<number>(),
      },
      generatorEngines: {
        ...generateGeneratorEnginesArrays(data.generatorEngines),
        ...generateTurboGenerator(data.generatorEngines),
      },
      shaftGeneratorPTO: generateShaftGeneratorPTO(data.shaftGeneratorPTO),
      // V1 carries no scrubber field. Its Yes/No radio saves In Use as false.
      scrubber: {
        inUse: initStateField<boolean>(false),
        mode: initStateField<string>(),
        runningHours: initStateField<number>(),
      },
      environmental: {
        incineratorMalfunctional: initStateField<boolean>(data.environmental.incineratorMalfunctional ?? false),
        odmeMalfunctional: initStateField<boolean>(data.environmental.odmeMalfunctional ?? false),
        bwtsMalfunctional: initStateField<boolean>(data.environmental.bwtsMalfunctional ?? false),
        scrubberMalfunctional: initStateField<boolean>(data.environmental.scrubberMalfunctional ?? false),
        owsMalfunctional: initStateField<boolean>(data.environmental.owsMalfunctional ?? false),
      },
      shorePower: generateCarryOverShorePower(data.shorePower),
      cargoPumpMeters: {
        pumps: generateCargoPumpsForCarryOver(data.cargoPumpsKwh, vesselSpec),
      },
    },
    calculations: {} as Record<string, CalculationStatus>,
  };
};

export const PowerStore = signalStore(
  { providedIn: 'root' },
  withDevtools('PowerStore'),
  withState(initBlank()),
  withState({ pendingEdnFiles: {} as Partial<Record<EdnFileType, File>> }),
  withState({ uploadingEdnFiles: {} as Partial<Record<EdnFileType, boolean>> }),
  withPowerEffects(),
  withFormStoreReducer(powerEvents, initPowerStateReducer, 'power'),
  withPowerReducer(),
  withShorePowerDocumentReducer(),
  withTcRpmPowerReducer(),
  withCalculationReducer(
    'tcRpmPower',
    powerCalculationEvents.getTcRpmPower,
    powerCalculationEvents.getTcRpmPowerSuccess,
    powerCalculationEvents.getTcRpmPowerFailed
  ),
  withComputed((store) => {
    const vesselSpecStore = inject(VesselSpecStore);
    const operationalStore = inject(OperationalStore);
    return {
      vm: computed(() =>
        buildPowerVm(
          store.formState(),
          vesselSpecStore.vesselSpec(),
          operationalStore.vm().reportPeriod('reportTime'),
          operationalStore.formState().vesselDetails?.reportNumber.value,
          operationalStore.vm().reportFrom,
          operationalStore.vm().reportTo
        )
      ),
    };
  }),
  withFeature((store) =>
    withCalculatedValues(powerEvents, () =>
      collectPowerCalculatedValues(store.formState(), store.vm(), store.tcRpmPowerResult())
    )
  )
);
